import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * PublicRoute Component
 * Componente de guarda para rotas públicas (ex: login)
 * Redireciona usuários autenticados para o dashboard
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { destinoPosLogin } from '../../utils/destinoSeguro';
// ============================================================================
// Component
// ============================================================================
/**
 * PublicRoute
 *
 * Componente de rota pública que impede usuários autenticados de acessar.
 * Usado principalmente para páginas de login.
 *
 * Comportamento:
 * - Se loading: Exibe spinner de carregamento
 * - Se autenticado OU tem dados no localStorage: Redireciona para /app/inicio
 * - Se não autenticado: Renderiza children (página de login)
 *
 * Verificação Dupla:
 * - Verifica isAuthenticated do AuthContext
 * - Verifica localStorage como fallback (caso AuthContext não tenha detectado ainda)
 *
 * @example
 * ```tsx
 * <PublicRoute>
 *   <LoginPage />
 * </PublicRoute>
 * ```
 */
export const PublicRoute = ({ children }) => {
    const { isAuthenticated, loading, pendingRecoveryCodes = [] } = useAuth();
    const location = useLocation();
    const destino = destinoPosLogin(location);
    const showingRecoveryCodes = pendingRecoveryCodes.length > 0;
    // Verificação direta do localStorage como fallback
    const userDataInStorage = localStorage.getItem('userData');
    const hasUserData = userDataInStorage && userDataInStorage !== 'null';
    // Se há dados no localStorage mas o AuthContext ainda não detectou, redirecionar
    if (hasUserData && !loading && !showingRecoveryCodes) {
        return _jsx(Navigate, { to: destino, replace: true });
    }
    // Loading state - exibe spinner enquanto verifica autenticação
    if (loading) {
        return (_jsx("div", { className: "min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1a1a1a] to-[#020202] transition-colors duration-300", children: _jsxs("div", { className: "text-center", children: [_jsx("div", { className: "animate-spin rounded-full h-12 w-12 border-b-2 border-icavi-500 mx-auto mb-4" }), _jsx("p", { className: "text-white text-lg", children: "Carregando..." })] }) }));
    }
    // Se o usuário está autenticado, redirecionar para o dashboard
    if (isAuthenticated && !showingRecoveryCodes) {
        return _jsx(Navigate, { to: destino, replace: true });
    }
    // Se não está autenticado, mostrar a página pública (login)
    return _jsx(_Fragment, { children: children });
};
export default PublicRoute;
